import { BaseThreeScene } from '@modules/three/common/BaseThreeScene';
import { threeStore } from '@modules/three/store/threeStore';
import { prepareSavedGumGeometry } from '@modules/three/common/halfEdgeMesh/prepareSavedGumGeometry';
import JawCorrection from '@correction/three/JawCorrection';
import type { JawsSegmentationResponse } from '@/schema/jawsSegmentation';
import type { JawSegmentationResponse } from '@/schema/jawSegmentation';
import type { JawsCorrectionResponse } from '@/schema/JawsCorrection';
import type { JawCorrectionResponse } from '@/schema/JawCorrection';

/** Wall thickness of the printed shell, in millimetres. */
export const PRINT_WALL_MM = 1;

export type PrintableJawModel = {
  jaw: 'upper' | 'lower';
  /** Binary STL, oriented Z-up and ready for the slicer. */
  blob: Blob;
  filename: string;
};

export type PrintableBuildOptions = {
  thicknessMm?: number;
  /** Progress for a UI that has a minute to fill. Called with short English phrases. */
  onStage?: (stage: string) => void;
  signal?: AbortSignal;
};

/**
 * Rebuilds a SAVED correction off-screen and hollows it with the voxel cavity, so a
 * case can be sent to production without reopening the Braces Designer.
 *
 * The reconstruction is the same one the export phase does (see ExportService): crowns
 * come from the segmentation and are placed by the per-tooth deltas the correction
 * stored, while the gum is the correction's own PLY — already deformed around those
 * crowns. Nothing is re-solved, so what prints is what the orthodontist approved and
 * not a second opinion computed from the same inputs.
 *
 * Expect tens of seconds per jaw: the cavity marches a field of ~20 M cells. Call it
 * from a place that can show progress.
 */
export async function buildPrintableJawModels(
  segmentation: JawsSegmentationResponse,
  correction: JawsCorrectionResponse,
  options: PrintableBuildOptions = {},
): Promise<PrintableJawModel[]> {
  const thicknessMm = options.thicknessMm ?? PRINT_WALL_MM;
  const stage = (message: string) => options.onStage?.(message);
  const checkAborted = () => {
    if (options.signal?.aborted) throw new DOMException('Print model build cancelled', 'AbortError');
  };

  stage('Loading meshes');
  const host = new OffscreenSceneHost();
  try {
    const results: PrintableJawModel[] = [];
    for (const jaw of ['upper', 'lower'] as const) {
      checkAborted();
      stage(`Rebuilding ${jaw} jaw`);
      const jawCorrection = await buildJaw(
        jaw,
        jaw === 'upper' ? segmentation.upperJaw : segmentation.lowerJaw,
        jaw === 'upper' ? correction.upperJaw : correction.lowerJaw,
      );
      try {
        checkAborted();
        stage(`Hollowing ${jaw} jaw — this takes a while`);
        // joinInterdentalGum stays at its default (on). Turning it off would strip the
        // gum's generated faces by an index taken from the SEGMENTATION, while the gum
        // loaded here is the correction's own PLY — a different face order, so the
        // marker would cut somewhere arbitrary.
        const stl = await jawCorrection.exportFusedShellToSTL(thicknessMm, 'mesh', {
          interior: 'voxel',
        });
        results.push({
          jaw,
          blob: new Blob([stl], { type: 'model/stl' }),
          filename: `${jaw}-jaw-week-${correction.treatmentWeek}.stl`,
        });
      } finally {
        jawCorrection.disposeArap();
      }
    }
    stage('Done');
    return results;
  } finally {
    host.dispose();
  }
}

/**
 * What the stored poses actually ask for, in the units a person can judge.
 *
 * Worth a line in the console every run: a plan that moves one tooth by a degree
 * a week produces models that look identical side by side, and without a number
 * there is no way to tell that apart from a rebuild that dropped the poses on the
 * floor. Reads the DTO, not the scene, so it says what was ASKED for.
 */
function describePose(correction: JawCorrectionResponse): string {
  let maxMm = 0;
  let maxDeg = 0;
  let movedFdi = -1;
  for (const tooth of correction.teethCorrection) {
    const [mx = 0, my = 0, mz = 0] = tooth.movement ?? [];
    const mm = Math.hypot(mx, my, mz);
    const [qx = 0, qy = 0, qz = 0, qw = 1] = tooth.rotation ?? [];
    const deg = (2 * Math.atan2(Math.hypot(qx, qy, qz), Math.abs(qw)) * 180) / Math.PI;
    if (mm > maxMm || deg > maxDeg) movedFdi = tooth.fdi;
    maxMm = Math.max(maxMm, mm);
    maxDeg = Math.max(maxDeg, deg);
  }
  if (maxMm === 0 && maxDeg === 0) return 'no tooth moves in this week — the model is the scan pose';
  return `largest move ${maxMm.toFixed(3)} mm / ${maxDeg.toFixed(2)}° (FDI ${movedFdi})`;
}

async function buildJaw(
  jawType: 'upper' | 'lower',
  segmentation: JawSegmentationResponse,
  correction: JawCorrectionResponse,
): Promise<JawCorrection> {
  const [gum] = await BaseThreeScene.loadGeometries([correction.model3dUrl]);
  const crowns = await BaseThreeScene.loadGeometries(
    segmentation.teethSegmentation.map((tooth) => tooth.model3dUrl),
  );

  const prep = prepareSavedGumGeometry(gum);
  if (prep.collapsedDropped > 0) {
    console.log(
      `buildPrintableJawModels (${jawType}): dropped ${prep.collapsedDropped} collapsed gum triangle(s)`,
    );
  }
  if (prep.coincidentNudged > 0) {
    console.log(
      `buildPrintableJawModels (${jawType}): welding the gum's coincident vertices would pinch ` +
        `it — kept the saved PLY topology instead, ${prep.coincidentNudged} vertex(es) nudged apart`,
    );
  }

  const jaw = new JawCorrection(gum, crowns, segmentation, jawType, { gumAlreadyPosed: true });
  const placed = jaw.applyToothCorrections(correction.teethCorrection);
  console.log(
    `buildPrintableJawModels (${jawType}): ${placed} pose(s) applied, ${describePose(correction)}`,
  );
  if (placed !== correction.teethCorrection.length) {
    // Loud rather than silent: a crown left in its scan position would be printed in
    // the wrong place, and the shell around it would look entirely plausible.
    console.warn(
      `buildPrintableJawModels (${jawType}): placed ${placed} of ` +
        `${correction.teethCorrection.length} stored tooth pose(s) — the rest have an FDI ` +
        'this jaw does not carry',
    );
  }
  return jaw;
}

/**
 * The correction classes put themselves into `threeStore.rootState.scene` on
 * construction, so building one needs a scene even when nothing will be drawn. This
 * mounts the smallest real one off-screen and puts the previous root state back on the
 * way out, so a viewer elsewhere on the page keeps working.
 */
class OffscreenSceneHost {
  private readonly container: HTMLDivElement;
  private readonly scene: BaseThreeScene;
  private readonly previousRootState = threeStore.getState().rootState;

  constructor() {
    this.container = document.createElement('div');
    this.container.style.cssText = 'position:fixed;left:-10000px;top:0;width:2px;height:2px';
    document.body.appendChild(this.container);
    try {
      // Never started: no render loop runs, the WebGL context exists only because
      // BaseThreeScene owns it. All the work downstream is CPU-side geometry.
      this.scene = new BaseThreeScene(this.container);
    } catch (error) {
      this.container.remove();
      throw error;
    }
  }

  dispose() {
    this.scene.dispose();
    this.container.remove();
    threeStore.setState({ rootState: this.previousRootState });
  }
}
